/**
 * Detects transient browser/network errors that typically have no actionable
 * application stack frames. These are often produced by Next.js route payload
 * fetch retries/fallbacks during navigation.
 */
export const isKnownTransientNetworkError = (error: {
  message?: string;
  stack?: string;
}): boolean => {
  const normalizedMessage = error.message?.toLowerCase().trim() ?? "";
  const normalizedStack = error.stack?.toLowerCase().trim() ?? "";

  if (_isRscPayloadFallbackError(normalizedMessage)) {
    return true;
  }

  if (_isTurbopackChunkInferenceError(normalizedMessage, normalizedStack)) {
    return true;
  }

  if (_isServerActionUnexpectedResponse(normalizedMessage, normalizedStack)) {
    return true;
  }

  if (_isServerActionNotFoundError(normalizedMessage, normalizedStack)) {
    return true;
  }

  if (_isWrappedFetchNetworkError(normalizedMessage, normalizedStack)) {
    return true;
  }

  if (_isSuspenseBoundaryFallbackError(normalizedMessage)) {
    return true;
  }

  if (_isRscConnectionClosedError(normalizedMessage, normalizedStack)) {
    return true;
  }

  const hasActionableStackFrame = normalizedStack
    .split("\n")
    .some(
      (line) =>
        line.trim().startsWith("at ") &&
        !_isMinifiedNextChunkFrame(line) &&
        !_isSourcelessNativeFrame(line),
    );

  if (hasActionableStackFrame) {
    return false;
  }

  if (_isGenericNetworkFetchError(normalizedMessage, normalizedStack)) {
    return true;
  }

  if (_isCompoundFetchNetworkError(normalizedMessage)) {
    return true;
  }

  return false;
};

function _isRscPayloadFallbackError(normalizedMessage: string): boolean {
  return (
    normalizedMessage.includes("failed to fetch rsc payload") &&
    normalizedMessage.includes("falling back to browser navigation")
  );
}

function _isTurbopackChunkInferenceError(
  normalizedMessage: string,
  normalizedStack: string,
): boolean {
  const pattern = "can't infer type of chunk from url";
  return (
    normalizedMessage.includes(pattern) || normalizedStack.includes(pattern)
  );
}

/**
 * Detects the Next.js "An unexpected response was received from the server"
 * error thrown by server-action-reducer.ts. This occurs when a server action
 * response has an unexpected content-type (not text/x-component), typically
 * caused by deployment version skew between cached client JS and the new
 * server, or transient infrastructure issues. The error contains only
 * framework stack frames (no application code) and is resolved by a page
 * refresh.
 *
 * In production builds the stack is minified so we cannot rely on
 * "server-action-reducer" appearing in the stack trace. The error message
 * itself is unique enough to Next.js internals that matching on it alone is
 * safe.
 */
function _isServerActionUnexpectedResponse(
  normalizedMessage: string,
  _normalizedStack: string,
): boolean {
  return normalizedMessage.includes(
    "an unexpected response was received from the server",
  );
}

/**
 * Detects the Next.js UnrecognizedActionError thrown when a client bundle
 * references a Server Action ID that no longer exists on the server. This
 * happens after a deployment when stale browser tabs invoke actions from
 * the previous build. The error originates from
 * `server-action-reducer.ts` and is resolved by a full page reload.
 */
function _isServerActionNotFoundError(
  normalizedMessage: string,
  _normalizedStack: string,
): boolean {
  return (
    normalizedMessage.includes("was not found on the server") ||
    normalizedMessage.includes("failed to find server action")
  );
}

/**
 * Detects Datadog RUM-wrapped network resource errors. When the browser's
 * fetch for a page/RSC payload fails, Datadog RUM reports the error with
 * a synthetic message of the form "Fetch error GET <url>" while the
 * original browser error (e.g. Firefox's "NetworkError when attempting to
 * fetch resource.") is only present in the stack. These are transient
 * network errors with no actionable application stack frames. Minified
 * `_next/static/chunks/` frames (tRPC httpBatchLink, React Query) are
 * vendor internals, not application source, so they are not treated as
 * actionable.
 */
function _isWrappedFetchNetworkError(
  normalizedMessage: string,
  normalizedStack: string,
): boolean {
  if (!normalizedMessage.startsWith("fetch error")) {
    return false;
  }

  const TRANSIENT_NETWORK_SNIPPETS = [
    "networkerror when attempting to fetch resource",
    "failed to fetch",
    "load failed",
    "network error",
    "error in input stream",
  ];

  const hasTransientSignalInStack = TRANSIENT_NETWORK_SNIPPETS.some((snippet) =>
    normalizedStack.includes(snippet),
  );

  if (!hasTransientSignalInStack) {
    return false;
  }

  const hasActionableStackFrame = normalizedStack
    .split("\n")
    .some(
      (line) =>
        line.trim().startsWith("at ") &&
        !line.includes("networkerror") &&
        !line.includes("failed to fetch") &&
        !line.includes("load failed") &&
        !line.includes("network error") &&
        !_isMinifiedNextChunkFrame(line) &&
        !_isSourcelessNativeFrame(line),
    );

  return !hasActionableStackFrame;
}

/**
 * Matches generic browser-level network fetch failures that carry no
 * diagnostic value (transient connectivity, browser tab backgrounded, etc.).
 *
 * Covers the three major browser engines:
 *  - Firefox: "NetworkError when attempting to fetch resource."
 *             "Error in input stream" (body stream / gzip-brotli read
 *             failure, often on flaky networks or aborted RSC streams)
 *  - Chrome:  "Failed to fetch" / "network error"
 *  - Safari:  "Load failed"
 *
 * Uses exact matching to avoid false positives on application log messages
 * that contain these phrases as substrings (e.g. "Failed to fetch Zoom
 * meeting participants").
 */
const EXACT_BROWSER_NETWORK_ERRORS = [
  "networkerror when attempting to fetch resource.",
  "networkerror when attempting to fetch resource",
  "failed to fetch",
  "load failed",
  "network error",
  "error in input stream",
];

const STACK_BROWSER_NETWORK_ERRORS = EXACT_BROWSER_NETWORK_ERRORS.map(
  (msg) => `typeerror: ${msg}`,
);

function _isGenericNetworkFetchError(
  normalizedMessage: string,
  normalizedStack: string,
): boolean {
  if (EXACT_BROWSER_NETWORK_ERRORS.includes(normalizedMessage)) {
    return true;
  }

  return STACK_BROWSER_NETWORK_ERRORS.some((pattern) =>
    normalizedStack.includes(pattern),
  );
}

/**
 * Detects React Suspense boundary SSR fallback errors. In production builds
 * these appear as "Minified React error #419" (or the full message in dev:
 * "The server could not finish this Suspense boundary, likely due to an error
 * during server rendering. Switched to client rendering.").
 *
 * This is a recoverable situation — React automatically falls back to client-
 * side rendering when a Suspense boundary fails on the server. The error is
 * logged by the browser but the page still renders correctly. Treating it as
 * transient avoids showing the global error page for what is really a
 * graceful degradation.
 */
/**
 * Detects fetch errors wrapped by third-party scripts or browser extensions
 * that combine a "fetch" keyword with a network error keyword in a single
 * message (e.g. "Failed to fetch API data: Network Error"). These do not
 * match the exact patterns in `_isGenericNetworkFetchError` but are equally
 * non-actionable when there are no stack frames.
 *
 * Only called after `hasActionableStackFrame` has already returned false.
 */
function _isCompoundFetchNetworkError(normalizedMessage: string): boolean {
  const FETCH_INDICATORS = ["failed to fetch", "fetch error", "fetch failed"];
  const NETWORK_ERROR_INDICATORS = [
    "network error",
    "networkerror",
    "load failed",
  ];

  const hasFetchSignal = FETCH_INDICATORS.some((s) =>
    normalizedMessage.includes(s),
  );
  const hasNetworkSignal = NETWORK_ERROR_INDICATORS.some((s) =>
    normalizedMessage.includes(s),
  );

  return hasFetchSignal && hasNetworkSignal;
}

/**
 * Detects the "Connection closed." error thrown by React's RSC streaming
 * client (`react-server-dom-webpack-client.browser.production.js`).
 *
 * This occurs when the server-to-client RSC flight stream is interrupted
 * before the browser finishes reading it — typically caused by network
 * drops (cellular, backgrounded tab), the user navigating away mid-stream,
 * or a server-side redirect during SSR. The only stack frame is a minified
 * function inside the Next.js chunk that bundles the React flight client,
 * so there is nothing actionable for application code.
 *
 * A page reload resolves the issue.
 */
function _isRscConnectionClosedError(
  normalizedMessage: string,
  normalizedStack: string,
): boolean {
  if (normalizedMessage !== "connection closed.") {
    return false;
  }

  if (!normalizedStack) {
    return true;
  }

  const frames = normalizedStack
    .split("\n")
    .filter((line) => line.trim().startsWith("at "));

  if (frames.length === 0) {
    return true;
  }

  return frames.every(
    (frame) =>
      frame.includes("react-server-dom-webpack") ||
      frame.includes("react-dom") ||
      frame.includes("next/dist/") ||
      frame.includes("node_modules") ||
      _isMinifiedNextChunkFrame(frame),
  );
}

function _isMinifiedNextChunkFrame(frame: string): boolean {
  if (!frame.includes("_next/static/chunks/")) {
    return false;
  }
  return (
    !frame.includes("/src/") &&
    !frame.includes("/app/") &&
    !frame.includes("/lib/") &&
    !frame.includes("/components/")
  );
}

/**
 * Detects stack frames the JS engine emits with no source location at all.
 * `new Promise`, native builtins and similar engine internals are reported as
 * `<anonymous>` or `[native code]` with no URL, file or line/column, so they
 * cannot point at application code and must not make an error look
 * actionable.
 *
 * The concrete case: the Twilio Voice SDK's telemetry publisher wraps its
 * `fetch` in `new Promise`, so a failed publish arrives as minified vendor
 * chunk frames plus a bare `at new Promise @ <anonymous>`.
 *
 * A frame that merely mentions one of these markers while still naming a
 * source (e.g. `at Object.<anonymous> (src/app/foo.ts:1:2)`) does have a
 * location and stays actionable.
 */
const SOURCELESS_FRAME_MARKERS = ["<anonymous>", "[native code]"];

function _isSourcelessNativeFrame(frame: string): boolean {
  if (!SOURCELESS_FRAME_MARKERS.some((marker) => frame.includes(marker))) {
    return false;
  }

  const frameWithoutMarkers = SOURCELESS_FRAME_MARKERS.reduce(
    (remaining, marker) => remaining.split(marker).join(" "),
    frame,
  );

  return (
    !frameWithoutMarkers.includes("/") && !/:\d+:\d+/.test(frameWithoutMarkers)
  );
}

function _isSuspenseBoundaryFallbackError(normalizedMessage: string): boolean {
  return (
    normalizedMessage.includes(
      "the server could not finish this suspense boundary",
    ) ||
    normalizedMessage.includes("minified react error #419") ||
    normalizedMessage.includes("react.dev/errors/419")
  );
}
